/**
 * Appsmith-style **segmented resource picker** (ADR-0044 U-track) — the owner's
 * "Connect to query" reference: a trigger button → a popover with a SEARCH box +
 * the declared page queries GROUPED BY TYPOLOGY (Tabelle / Dataset / HTTP / JS /
 * Statici), each kind with its own icon. Replaces the flat `<Select>` of query
 * names so the operator browses by type as the resources grow.
 *
 * Pure-presentational over `lib/resourceKinds` (the grouping is unit-tested
 * there); reused by BOTH the data-source field (`data` prop) and the `run_query`
 * action picker — same browse experience everywhere.
 */
import { Boxes, Braces, Check, ChevronDown, Database, FileJson, Globe, Search, Server, Table2 } from "lucide-react";
import * as React from "react";

import { groupResources, type ResourceQuery } from "@/lib/resourceKinds";

import { FloatingPopover } from "./FloatingPopover";

/** lucide icon NAME (from `RESOURCE_KIND_META`) → the component. */
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Server,
  Table2,
  Boxes,
  Globe,
  Braces,
  FileJson,
  Database,
};

export interface ResourcePickerProps {
  /** The declared page queries (name + source_kind) to browse. */
  queries: ResourceQuery[];
  /** The currently-selected query name ("" = none). */
  value: string;
  onSelect: (name: string) => void;
  /** Placeholder on the trigger when nothing is picked. */
  placeholder?: string;
  /** Menu edge alignment. */
  align?: "left" | "right";
}

export function ResourcePicker({
  queries,
  value,
  onSelect,
  placeholder = "— scegli una query —",
}: ResourcePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const btnRef = React.useRef<HTMLButtonElement>(null);

  // Reset the search each time the popover opens (fresh browse).
  React.useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const groups = groupResources(queries, search);
  const empty = queries.length === 0;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full min-w-[140px] items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
      >
        <span className={value ? "" : "text-muted-foreground"}>{value || placeholder}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>

      <FloatingPopover anchorRef={btnRef} open={open} onClose={() => setOpen(false)}>
        <div
          role="listbox"
          className="portal-pop max-h-72 w-[min(20rem,90vw)] overflow-hidden rounded-md border border-border bg-elevated shadow-xl"
        >
          {/* search */}
          <div className="flex items-center gap-2 border-b border-border/60 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              aria-label="Cerca query"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca…"
              className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div className="max-h-56 overflow-auto p-1">
            {empty && (
              <p className="px-2 py-2 text-xs text-muted-foreground">
                Nessuna query. Creane una nella scheda <strong>Query</strong> del pannello Dati.
              </p>
            )}
            {!empty && groups.length === 0 && (
              <p className="px-2 py-2 text-xs text-muted-foreground">Nessun risultato.</p>
            )}
            {groups.map((g) => {
              const Icon = ICONS[g.icon] ?? Database;
              return (
                <div key={g.kind} className="mb-1 last:mb-0">
                  <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                    <Icon className="h-3 w-3" />
                    {g.label}
                  </div>
                  {g.queries.map((q) => {
                    const active = q.name === value;
                    return (
                      <button
                        key={q.name}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          onSelect(q.name);
                          setOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs ${
                          active
                            ? "bg-accent/15 text-foreground"
                            : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                        }`}
                      >
                        <span className="truncate">{q.name}</span>
                        {active && <Check className="h-3.5 w-3.5 shrink-0 text-accent" />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </FloatingPopover>
    </div>
  );
}
